import { useCallback, useEffect, useState } from "react";
import NotFound from "@/pages/NotFound";
import { useAdmin } from "@/context/AdminContext";
import { useMaintenance } from "@/context/MaintenanceContext";
import { api } from "@/lib/api";
import { tzAbbr, tzCity, viewerTz } from "@/lib/time";
import { LiveControl } from "@/components/maintenance/admin/LiveControl";
import { ScheduleList } from "@/components/maintenance/admin/ScheduleList";
import { ScheduleDialog } from "@/components/maintenance/admin/ScheduleDialog";
import { HistoryLog, PreviewLink, VisitorSettings } from "@/components/maintenance/admin/Panels";
import { AutoMaintenance } from "@/components/maintenance/admin/AutoMaintenance";
import { ComingSoonControl } from "@/components/maintenance/admin/ComingSoonControl";

export default function MaintenancePage() {
  const { can, user } = useAdmin();
  const { refresh: refreshPublic } = useMaintenance();
  const [data, setData] = useState(null);
  const [count, setCount] = useState(0);
  const [dialog, setDialog] = useState({ open: false, window: null });

  const load = useCallback(() => {
    api.get("/maintenance").then((r) => setData(r.data && typeof r.data === "object" ? r.data : null)).catch(() => {});
    refreshPublic();
  }, [refreshPublic]);

  useEffect(() => {
    if (!can("system_settings")) return undefined;
    load();
    api.get("/newsletter/audience-count").then((r) => setCount(r.data?.count || 0)).catch(() => {});
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [can, load]);

  if (user === null) return <div className="min-h-[60vh]" />;
  if (!can("system_settings")) return <NotFound />;
  const tz = viewerTz();

  return (
    <div data-testid="maintenance-page" className="mx-auto max-w-6xl px-5 pb-28 pt-10 sm:px-8 lg:px-12">
      <p className="font-mono-label text-xs uppercase tracking-[0.25em] text-[#57534E]">Private · only you can see this</p>
      <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">Site status & maintenance</h1>
      <p className="mt-3 text-sm text-[#57534E]" data-testid="maintenance-your-tz">Times are shown in your time zone: {tzCity(tz)} ({tzAbbr(new Date().toISOString(), tz)}). You always see the full site while logged in.</p>
      {!data ? <div className="mt-10 h-48 animate-pulse bg-black/5" /> : (
        <div className="mt-10 space-y-14">
          <ComingSoonControl settings={data.settings || {}} onSaved={load} />
          <LiveControl status={data.status || {}} onDone={load} />
          <ScheduleList windows={Array.isArray(data.windows) ? data.windows : []} onNew={() => setDialog({ open: true, window: null })} onEdit={(w) => setDialog({ open: true, window: w })} onChanged={load} />
          <AutoMaintenance />
          <div className="grid gap-6 lg:grid-cols-2">
            <VisitorSettings settings={data.settings || {}} onSaved={load} />
            <PreviewLink token={data.settings?.preview_token || ""} onChanged={load} />
          </div>
          <HistoryLog log={Array.isArray(data.log) ? data.log : []} onChanged={load} />
        </div>
      )}
      <ScheduleDialog open={dialog.open} window={dialog.window} defaults={data?.settings} subscriberCount={count}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={load} />
    </div>
  );
}
